import React, { useEffect, useRef, useState } from 'react';
import { Col, Row } from 'reactstrap';
import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import FormSelectField from '../../common/components/formFields/FormSelectField';
import { constantToSelect } from '../../common/helpers/constantToSelect';
import FormCurrencyInputField from '../../common/components/formFields/FormCurrencyInputField';
import { AuctionTime, AuctionTimeName } from '../../constants/AuctionTime';
import {
  AuctionStatusType,
  AuctionStatusTypeName,
  AvailableAuctionStatuses,
} from '../../constants/AuctionStatusType';
import FormGroupRadioField from '../../common/components/formFields/FormGroupRadioField';
import {
  AuctionPublishType,
  AuctionPublishTypeName,
} from '../../constants/AuctionPublishType';
import FormDatePickerField from '../../common/components/formFields/FormDatePickerField';
import {
  fetchOrganizationGroup,
  fetchOrganizationLocations,
  getDraft,
  saveDescriptionAndSettingsDraft,
  validateDescriptionAndSettings,
  validateMedia,
} from '../AddVehicleActions';
import { reverseDateToString } from '../../common/utils/date/Date.utils';
import { checkIsFieldEmpty } from '../../common/helpers/checkIsFieldEmpty';
import { parseCurrencyToIntIfString } from '../../common/helpers/parseCurrencyToIntIfString';
import { DueInDateType, DueInDateTypeName } from '../../constants/DueInDateType';
import AddVehicleHeader from '../AddVehicleHeader';
import AddVehicleContentWrapper from '../AddVehicleContentWrapper';
import { prepareSelectLabel } from '../../common/helpers/prepareSelectLabel';
import { prepareTryingToDealDeliveryTypeLabel } from '../../common/helpers/prepareTryingToDealDeliveryTypeLabel';
import validationSchema from './LaunchVehicleForm.schema';
import { fetchOrganizationDetails } from '../../settings/SettingsActions';
import AddVehicleFooter from '../AddVehicleFooter';
import {
  publishAuction,
  republishAuction,
  setVehicleFilter,
} from '../../myvehicles/MyVehiclesActions';
import { AuctionStatus } from '../../constants/AuctionStatus';
import history from '../../browserHistory';
import RouterPaths from '../../constants/RouterPaths';
import {
  ADD_DRAFT_VALIDATION_ERRORS,
  VALIDATE_DESCRIPTION_AND_SETTINGS_SUCCESS,
} from '../AddVehicleReducer';
import { validateLaunchData } from '../helpers/draftValidator';
import { SSE_SELLER, SSEClient } from '../../api/SSEClient';
import { AddVehicleNavMapping, AddVehicleSteps } from '../../constants/AddVehicle';
// import {REQUIRED_PHOTOS_AMOUNT} from "../../constants/RequiredPhotosAmount";

const sseClientSeller = new SSEClient(SSE_SELLER);

const auctionTimes = constantToSelect(AuctionTimeName);
const auctionTypes = constantToSelect(
  Object.keys(AuctionStatusType)
    .filter(el => AvailableAuctionStatuses.includes(el))
    .reduce((obj, key) => {
      return {
        ...obj,
        [key]: AuctionStatusTypeName[key],
      };
    }, {}),
);

const auctionPublishTypes = constantToSelect(AuctionPublishTypeName);
const dueInDateTypes = constantToSelect(DueInDateTypeName);

const LaunchVehicleForm = ({ form, prevForm, launchVehicleHandler }) => {
  const dispatch = useDispatch();
  const valuesRef = useRef();
  const isLoading = useSelector(state => state.navbarLoader.get('isLoading'));
  const organizationLocations = useSelector(state =>
    state.addVehicle.get('organizationLocations'),
  );
  const organizationGroup = useSelector(state => state.addVehicle.get('organizationGroup'));
  const validationErrors = useSelector(state => state.addVehicle.get('validationErrors'));
  const id = useSelector(state => state.addVehicle.get('id'));
  const [isNextDisabled, setIsNextDisabled] = useState(true);

  useEffect(() => {
    form.setFieldValue('timeOption', form?.values?.timeOption?.value);
  }, []);

  useEffect(() => {
    const checkIfDisableNextButton = () => {
      const { PHYSICAL, MINIMUM_PRICE, TRYING_TO_DEAL } = AuctionStatusType;
      const {
        description,
        vehicleLocation,
        timeOption,
        type,
        minimumPriceInPence,
        autoAcceptPriceInPence,
        tryingToDealDeliveryDate,
        tryingToDealDeliveryDateType,
      } = form?.values;
      switch (form?.values?.type?.value) {
        case PHYSICAL: {
          checkIsFieldEmpty(
            [description, vehicleLocation, timeOption, type, autoAcceptPriceInPence],
            isDisabled => setIsNextDisabled(isDisabled),
          );
          break;
        }
        case MINIMUM_PRICE: {
          checkIsFieldEmpty(
            [description, vehicleLocation, timeOption, type, minimumPriceInPence],
            isDisabled => setIsNextDisabled(isDisabled),
          );
          break;
        }
        case TRYING_TO_DEAL: {
          checkIsFieldEmpty(
            [
              description,
              vehicleLocation,
              timeOption,
              type,
              tryingToDealDeliveryDateType,
              tryingToDealDeliveryDateType?.value === DueInDateType.CUSTOM_DATE
                ? tryingToDealDeliveryDate
                : true,
            ],
            isDisabled => setIsNextDisabled(isDisabled),
          );
          break;
        }
        default:
          return null;
      }
      return null;
    };
    if (form.values) {
      checkIfDisableNextButton();
    }
  }, [form]);

  const preparedOrganizationLocations =
    organizationLocations &&
    organizationLocations.map(item => {
      return {
        value: item?.id,
        label: item?.postalCode?.postcode,
      };
    });

  useEffect(() => {
    dispatch(fetchOrganizationLocations());
    dispatch(fetchOrganizationGroup());
  }, [dispatch]);

  const handleSaveDraft = () => {
    const {
      description,
      vehicleLocation,
      type,
      minimumPriceInPence,
      autoAcceptPriceInPence,
      tryingToDealDeliveryDate,
      tryingToDealDeliveryDateType,
      timeOption,
      publishTarget,
    } = form?.values;

    const descriptionAndSettings = {
      description,
      locationId: vehicleLocation?.value,
      type: type?.value,
      minimumPriceInPence:
        type?.value === AuctionStatusType.MINIMUM_PRICE
          ? parseCurrencyToIntIfString(minimumPriceInPence) * 100
          : null,
      autoAcceptPriceInPence:
        type?.value === AuctionStatusType.PHYSICAL
          ? parseCurrencyToIntIfString(autoAcceptPriceInPence) * 100
          : null,
      tryingToDealDeliveryDate:
        type?.value === AuctionStatusType.TRYING_TO_DEAL &&
        tryingToDealDeliveryDateType?.value === DueInDateType.CUSTOM_DATE
          ? reverseDateToString(tryingToDealDeliveryDate)
          : null,
      tryingToDealDeliveryDateType:
        type?.value === AuctionStatusType.TRYING_TO_DEAL
          ? tryingToDealDeliveryDateType?.value
          : null,
      timeOption: timeOption?.value,
      publishTarget,
    };

    dispatch(
      saveDescriptionAndSettingsDraft(descriptionAndSettings, () => dispatch(getDraft(id))),
    );
    form.setErrors({});
  };

  const handleStatusChange = e => {
    if (e.value === AuctionStatusType.TRYING_TO_DEAL) {
      form.setFieldValue('tryingToDealDeliveryDate', new Date());
    } else {
      form.setFieldValue('tryingToDealDeliveryDate', null);
    }
    form.setFieldValue('minimumPriceInPence', '');
    form.setFieldValue('autoAcceptPriceInPence', '');
  };

  useEffect(() => {
    if (form?.values) {
      valuesRef.current = form.values;
    }
  }, [form?.values]);

  useEffect(
    () => () => {
      dispatch({
        type: VALIDATE_DESCRIPTION_AND_SETTINGS_SUCCESS,
        payload: {
          descriptionAndSettings: {
            description: valuesRef.current?.description,
            defaultDescription: valuesRef.current?.defaultDescription,
            locationId: valuesRef.current?.vehicleLocation?.value,
            type: valuesRef.current?.type?.value,
            autoAcceptPriceInPence: valuesRef.current?.autoAcceptPriceInPence * 100,
            minimumPriceInPence: valuesRef.current?.minimumPriceInPence * 100,
            timeOption: valuesRef.current?.timeOption?.value,
            publishTarget: valuesRef.current?.publishTarget,
          },
        },
      });
      dispatch({
        type: ADD_DRAFT_VALIDATION_ERRORS,
        payload: {
          formStep: 11,
          errors: validateLaunchData(valuesRef.current),
        },
      });
    },
    [],
  );

  return (
    <>
      <AddVehicleHeader
        saveDraftButtonHandler={handleSaveDraft}
        saveDraftButtonDisabled={isLoading}
      />
      <AddVehicleContentWrapper>
        <Row>
          <Col>
            <h5 className="font-weight-bold m-0">Publish Listing</h5>
            <p className="text-sm mt-2">
              All vehicle details are now complete. Please select the parameters of your
              auction below.
            </p>
          </Col>
        </Row>
        <Form autoComplete="off" className="text-left mt-4">
          <Row>
            <Col md={4} sm={12}>
              <FormSelectField
                name="vehicleLocation"
                label="Location"
                options={[...preparedOrganizationLocations]}
                required
              />
            </Col>
            <Col md={4} sm={12}>
              <FormSelectField
                name="timeOption"
                label="Time"
                options={auctionTimes}
                required
              />
            </Col>
            <Col md={4} sm={12}>
              <FormSelectField
                name="type"
                label="Status"
                options={auctionTypes}
                onChangeHandler={handleStatusChange}
                required
              />
            </Col>
          </Row>
          <Row className="mt-3">
            {form?.values?.type?.value === AuctionStatusType.MINIMUM_PRICE && (
              <Col md={6} sm={12}>
                <FormCurrencyInputField
                  name="minimumPriceInPence"
                  label="No Reserve Price"
                  invalid={
                    !!form.errors.minimumPriceInPence ||
                    validationErrors?.vehicleDescriptionAndSettingsForm
                  }
                  required
                />
              </Col>
            )}
            {form?.values?.type?.value === AuctionStatusType.PHYSICAL && (
              <Col md={6} sm={12}>
                <FormCurrencyInputField
                  name="autoAcceptPriceInPence"
                  label="Reserve Price"
                  invalid={
                    !!form.errors.autoAcceptPriceInPence ||
                    validationErrors?.vehicleDescriptionAndSettingsForm
                  }
                  required
                />
              </Col>
            )}
            {form?.values?.type?.value === AuctionStatusType.TRYING_TO_DEAL && (
              <Col md={6} sm={12}>
                <FormSelectField
                  name="tryingToDealDeliveryDateType"
                  label="Due in date"
                  options={dueInDateTypes}
                  required
                />
              </Col>
            )}
            {form?.values?.type?.value === AuctionStatusType.TRYING_TO_DEAL &&
              form?.values?.tryingToDealDeliveryDateType?.value ===
                DueInDateType.CUSTOM_DATE && (
                <Col md={6} sm={12}>
                  <FormDatePickerField
                    name="tryingToDealDeliveryDate"
                    label="Delivery date"
                    error={
                      !!form.errors.tryingToDealDeliveryDate ||
                      validationErrors?.vehicleDescriptionAndSettingsForm
                    }
                    readOnly
                    required
                  />
                </Col>
              )}
          </Row>
          <Row>
            <Col md={12} className="pt-4">
              {organizationGroup && (
                <FormGroupRadioField
                  name="publishTarget"
                  radios={auctionPublishTypes}
                  classNames="auction-publish-radios"
                />
              )}
            </Col>
          </Row>
        </Form>
      </AddVehicleContentWrapper>
      <AddVehicleFooter
        nextButtonHandler={() => {
          form.handleSubmit();
        }}
        showLaunchVehicleButton
        launchVehicleHandler={launchVehicleHandler}
        backButtonHandler={prevForm}
        nextButtonDisabled={isLoading || isNextDisabled}
        launchVehicleButtonDisabled={isLoading || isNextDisabled}
        nextButtonText="SAVE"
      />
    </>
  );
};

const LaunchVehicle = ({ prevForm, generalValues, nextForm }) => {
  const dispatch = useDispatch();
  const [initialValues, setInitialValues] = useState({});
  const [relaunchAuction, setRelaunchAuction] = useState(false);
  const id = useSelector(state => state.addVehicle.get('id'));
  const isRepublishAllowed = useSelector(state => state.addVehicle.get('isRepublishAllowed'));
  const validationErrors = useSelector(state => state.addVehicle.get('validationErrors'));
  const descriptionAndSettings = useSelector(state =>
    state.addVehicle.get('descriptionAndSettings'),
  );
  const mainLocation = useSelector(
    state => state.myOrganization.get('organizationDetails')?.mainLocation,
  );
  // const photos = Array.from(useSelector(state => state.addVehicle.get('photos')));

  const organizationLocations = useSelector(state =>
    state.addVehicle.get('organizationLocations'),
  );

  const prepareTimeOptionSelect = () => {
    const {
      AUCTION_TIME_30,
      AUCTION_TIME_45,
      AUCTION_TIME_60,
      AUCTION_TIME_180,
      AUCTION_TIME_1440,
      AUCTION_TIME_2880,
      AUCTION_TIME_4320,
      AUCTION_TIME_5760,
      AUCTION_TIME_7200,
      AUCTION_TIME_8640,
      AUCTION_TIME_10080,
      AUCTION_TIME_11520,
      AUCTION_TIME_12960,
      AUCTION_TIME_14400,
      AUCTION_TIME_15840,
      AUCTION_TIME_17280,
      AUCTION_TIME_18720,
      AUCTION_TIME_20160,
    } = AuctionTime;

    const {
      AUCTION_TIME_30: AUCTION_TIME_30_LABEL,
      AUCTION_TIME_45: AUCTION_TIME_45_LABEL,
      AUCTION_TIME_60: AUCTION_TIME_60_LABEL,
      AUCTION_TIME_180: AUCTION_TIME_180_LABEL,
      AUCTION_TIME_1440: AUCTION_TIME_1440_LABEL,
      AUCTION_TIME_2880: AUCTION_TIME_2880_LABEL,
      AUCTION_TIME_4320: AUCTION_TIME_4320_LABEL,
      AUCTION_TIME_5760: AUCTION_TIME_5760_LABEL,
      AUCTION_TIME_7200: AUCTION_TIME_7200_LABEL,
      AUCTION_TIME_8640: AUCTION_TIME_8640_LABEL,
      AUCTION_TIME_10080: AUCTION_TIME_10080_LABEL,
      AUCTION_TIME_11520: AUCTION_TIME_11520_LABEL,
      AUCTION_TIME_12960: AUCTION_TIME_12960_LABEL,
      AUCTION_TIME_14400: AUCTION_TIME_14400_LABEL,
      AUCTION_TIME_15840: AUCTION_TIME_15840_LABEL,
      AUCTION_TIME_17280: AUCTION_TIME_17280_LABEL,
      AUCTION_TIME_18720: AUCTION_TIME_18720_LABEL,
      AUCTION_TIME_20160: AUCTION_TIME_20160_LABEL,
    } = AuctionTimeName;
    switch (descriptionAndSettings?.timeOption) {
      case AUCTION_TIME_30:
        return { label: AUCTION_TIME_30_LABEL, value: AUCTION_TIME_30 };
      case AUCTION_TIME_45:
        return { label: AUCTION_TIME_45_LABEL, value: AUCTION_TIME_45 };
      case AUCTION_TIME_60:
        return { label: AUCTION_TIME_60_LABEL, value: AUCTION_TIME_60 };
      case AUCTION_TIME_180:
        return { label: AUCTION_TIME_180_LABEL, value: AUCTION_TIME_180 };
      case AUCTION_TIME_1440:
        return { label: AUCTION_TIME_1440_LABEL, value: AUCTION_TIME_1440 };
      case AUCTION_TIME_2880:
        return { label: AUCTION_TIME_2880_LABEL, value: AUCTION_TIME_2880 };
      case AUCTION_TIME_4320:
        return { label: AUCTION_TIME_4320_LABEL, value: AUCTION_TIME_4320 };
      case AUCTION_TIME_5760:
        return { label: AUCTION_TIME_5760_LABEL, value: AUCTION_TIME_5760 };
      case AUCTION_TIME_7200:
        return { label: AUCTION_TIME_7200_LABEL, value: AUCTION_TIME_7200 };
      case AUCTION_TIME_8640:
        return { label: AUCTION_TIME_8640_LABEL, value: AUCTION_TIME_8640 };
      case AUCTION_TIME_10080:
        return { label: AUCTION_TIME_10080_LABEL, value: AUCTION_TIME_10080 };
      case AUCTION_TIME_11520:
        return { label: AUCTION_TIME_11520_LABEL, value: AUCTION_TIME_11520 };
      case AUCTION_TIME_12960:
        return { label: AUCTION_TIME_12960_LABEL, value: AUCTION_TIME_12960 };
      case AUCTION_TIME_14400:
        return { label: AUCTION_TIME_14400_LABEL, value: AUCTION_TIME_14400 };
      case AUCTION_TIME_15840:
        return { label: AUCTION_TIME_15840_LABEL, value: AUCTION_TIME_15840 };
      case AUCTION_TIME_17280:
        return { label: AUCTION_TIME_17280_LABEL, value: AUCTION_TIME_17280 };
      case AUCTION_TIME_18720:
        return { label: AUCTION_TIME_18720_LABEL, value: AUCTION_TIME_18720 };
      case AUCTION_TIME_20160:
        return { label: AUCTION_TIME_20160_LABEL, value: AUCTION_TIME_20160 };
      default:
        return null;
    }
  };

  useEffect(() => {
    dispatch(fetchOrganizationDetails());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getVehicleLocation = () => {
    const locations =
      organizationLocations &&
      organizationLocations?.map(item => {
        return {
          value: item?.id,
          label: item?.postalCode?.postcode,
        };
      });

    if (descriptionAndSettings?.locationId) {
      return locations?.find(item => item?.value === descriptionAndSettings?.locationId);
    }

    return locations?.find(item => item?.value === mainLocation?.id);
  };

  useEffect(() => {
    setInitialValues({
      description: descriptionAndSettings ? descriptionAndSettings?.description : '',
      defaultDescription: descriptionAndSettings?.defaultDescription
        ? descriptionAndSettings?.defaultDescription
        : '',
      vehicleLocation: getVehicleLocation(),
      type: (() => {
        if (
          descriptionAndSettings?.type &&
          AvailableAuctionStatuses.includes(descriptionAndSettings?.type)
        ) {
          return {
            label: AuctionStatusTypeName?.[descriptionAndSettings?.type]
              ? AuctionStatusTypeName?.[descriptionAndSettings?.type]
              : prepareSelectLabel(descriptionAndSettings?.type),
            value: descriptionAndSettings?.type,
          };
        }

        return {
          label: '',
          value: '',
        };
      })(),
      autoAcceptPriceInPence: descriptionAndSettings?.autoAcceptPriceInPence
        ? descriptionAndSettings?.autoAcceptPriceInPence / 100
        : '',
      minimumPriceInPence: descriptionAndSettings?.minimumPriceInPence
        ? descriptionAndSettings.minimumPriceInPence / 100
        : '',
      timeOption: descriptionAndSettings?.timeOption ? prepareTimeOptionSelect() : null,
      tryingToDealDeliveryDate: descriptionAndSettings?.tryingToDealDeliveryDate || new Date(),
      publishTarget: descriptionAndSettings?.publishTarget
        ? descriptionAndSettings?.publishTarget
        : AuctionPublishType.EVERYWHERE,
      tryingToDealDeliveryDateType: descriptionAndSettings?.tryingToDealDeliveryDateType
        ? {
            label: prepareTryingToDealDeliveryTypeLabel(
              descriptionAndSettings?.tryingToDealDeliveryDateType,
            ),
            value: descriptionAndSettings?.tryingToDealDeliveryDateType,
          }
        : null,
    });
  }, [descriptionAndSettings]);

  const handleSaveDraft = values => {
    const {
      description,
      vehicleLocation,
      type,
      minimumPriceInPence,
      autoAcceptPriceInPence,
      tryingToDealDeliveryDate,
      tryingToDealDeliveryDateType,
      timeOption,
      publishTarget,
    } = values;

    const descriptionAndSettings = {
      description,
      locationId: vehicleLocation?.value,
      type: type?.value,
      minimumPriceInPence:
        type?.value === AuctionStatusType.MINIMUM_PRICE
          ? parseCurrencyToIntIfString(minimumPriceInPence) * 100
          : null,
      autoAcceptPriceInPence:
        type?.value === AuctionStatusType.PHYSICAL
          ? parseCurrencyToIntIfString(autoAcceptPriceInPence) * 100
          : null,
      tryingToDealDeliveryDate:
        type?.value === AuctionStatusType.TRYING_TO_DEAL &&
        tryingToDealDeliveryDateType?.value === DueInDateType.CUSTOM_DATE
          ? reverseDateToString(tryingToDealDeliveryDate)
          : null,
      tryingToDealDeliveryDateType:
        type?.value === AuctionStatusType.TRYING_TO_DEAL
          ? tryingToDealDeliveryDateType?.value
          : null,
      timeOption: timeOption?.value,
      publishTarget,
    };

    dispatch(
      saveDescriptionAndSettingsDraft(descriptionAndSettings, () => {
        dispatch(setVehicleFilter(AuctionStatus.DRAFT));
        history.push(`${RouterPaths.MY_VEHICLES}#vehicleFilter`);
      }),
    );
  };

  const formErrorsExist = () => {
    const launchVehicleFormStep = AddVehicleNavMapping[
      AddVehicleSteps.LAUNCH_VEHICLE
    ][0].toString();
    if (Object.keys(validationErrors).includes(launchVehicleFormStep)) {
      delete validationErrors[launchVehicleFormStep];
    }
    return !!Object.values(validationErrors).filter(it => {
      if (typeof it === 'object') {
        return !!Object.keys(it).length;
      }
      return !!it;
    }).length;
  };

  const handleLaunchVehicle = () => {
    dispatch(
      getDraft(id, res => {
        const launchValues = {
          id: res?.id,
          version: res?.version,
          ...res?.descriptionAndSettings,
          autoAcceptPriceInPence: res?.descriptionAndSettings?.autoAcceptPriceInPence
            ? res.descriptionAndSettings?.autoAcceptPriceInPence / 100
            : null,
          minimumPriceInPence: res?.descriptionAndSettings?.minimumPriceInPence
            ? res.descriptionAndSettings?.minimumPriceInPence / 100
            : null,
        };
        if (isRepublishAllowed) {
          dispatch(
            republishAuction(
              launchValues,
              () => {
                if (sseClientSeller?.client?.readyState !== 1) {
                  setTimeout(() => {
                    dispatch(setVehicleFilter(AuctionStatus.PUBLISHED));
                    history.push(`${RouterPaths.MY_VEHICLES}#vehicleFilter`);
                  }, 1000);
                }
              },
              false,
            ),
          );
        } else {
          dispatch(
            publishAuction(
              launchValues,
              () => {
                if (sseClientSeller?.client?.readyState !== 1) {
                  setTimeout(() => {
                    dispatch(setVehicleFilter(AuctionStatus.PUBLISHED));
                    history.push(`${RouterPaths.MY_VEHICLES}#vehicleFilter`);
                  }, 1000);
                }
              },
              false,
            ),
          );
        }
      }),
    );
  };

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    if (formErrorsExist()) {
      handleSaveDraft(values);
    } else {
      setSubmitting(true);
      await dispatch(
        validateMedia(
          async () => {
            if (relaunchAuction) {
              await dispatch(
                validateDescriptionAndSettings(values, setErrors, handleLaunchVehicle),
              );
            } else {
              await dispatch(validateDescriptionAndSettings(values, setErrors));
            }
            setSubmitting(false);
          },
          () => {
            handleSaveDraft(values);
            setSubmitting(false);
          },
          true,
          true,
        ),
      );
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      validateOnBlur={false}
      validateOnChange={false}
      enableReinitialize
    >
      {form => (
        <LaunchVehicleForm
          form={form}
          prevForm={prevForm}
          launchVehicleHandler={() => {
            setRelaunchAuction(true);
            form.handleSubmit();
          }}
          generalValues={generalValues}
          nextForm={nextForm}
        />
      )}
    </Formik>
  );
};

LaunchVehicle.propTypes = {
  prevForm: PropTypes.func.isRequired,
  nextForm: PropTypes.func.isRequired,
};

export default LaunchVehicle;
